import { path } from 'ramda'

import { isAbortError } from '@common/utils'

const FEEDBACK_ATTACHMENTS_ADD_LOADING = 'FEEDBACK_ATTACHMENTS_ADD_LOADING'
const FEEDBACK_ATTACHMENTS_ADD_LOADED = 'FEEDBACK_ATTACHMENTS_ADD_LOADED'
const FEEDBACK_ATTACHMENTS_ADD_FAILED = 'FEEDBACK_ATTACHMENTS_ADD_FAILED'

const FEEDBACK_ATTACHMENTS_DELETE_LOADING = 'FEEDBACK_ATTACHMENTS_DELETE_LOADING'
const FEEDBACK_ATTACHMENTS_DELETE_LOADED = 'FEEDBACK_ATTACHMENTS_DELETE_LOADED'
const FEEDBACK_ATTACHMENTS_DELETE_FAILED = 'FEEDBACK_ATTACHMENTS_DELETE_FAILED'

const defaultState = {}

const entityName = 'feedbackAttachments'

const REQUEST_SIZE_LIMIT_MB = 20

const toBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.onload = () => {
      let encoded = reader.result.toString().replace(/^data:(.*,)?/, '')
      if (encoded.length % 4 > 0) {
        encoded += '='.repeat(4 - (encoded.length % 4))
      }
      resolve(encoded)
    }
    reader.onerror = (error) => reject(error)

    reader.readAsDataURL(file)
  })

export default {
  name: entityName,
  reducer: (state, action) => {
    if (action.type === FEEDBACK_ATTACHMENTS_ADD_LOADING) {
      return { ...state, add: { ...action.meta, payload: action.payload } }
    }
    if (action.type === FEEDBACK_ATTACHMENTS_ADD_LOADED) {
      return { ...state, add: { ...action.meta, data: action.payload } }
    }
    if (action.type === FEEDBACK_ATTACHMENTS_ADD_FAILED) {
      return { ...state, add: { ...action.meta, error: action.payload } }
    }
    if (action.type === FEEDBACK_ATTACHMENTS_DELETE_LOADING) {
      return { ...state, delete: { ...action.meta, payload: action.payload } }
    }
    if (action.type === FEEDBACK_ATTACHMENTS_DELETE_LOADED) {
      return { ...state, delete: { ...action.meta, data: action.payload } }
    }
    if (action.type === FEEDBACK_ATTACHMENTS_DELETE_FAILED) {
      return { ...state, delete: { ...action.meta, error: action.payload } }
    }
    return state || defaultState
  },
  selectFeedbackAttachmentsAddIsLoading: ({ feedbackAttachments }) => {
    const status = path(['add', 'status'], feedbackAttachments)
    return status === 'loading'
  },
  selectPropertyDetailsMetricsIsLoading: ({ propertyDetails }) => {
    const status = path(['metrics', 'status'], propertyDetails)
    return status === 'loading'
  },
  doAddFeedbackAttachments:
    (payload) =>
    async ({ dispatch, apiFetch }) => {
      try {
        dispatch({
          type: FEEDBACK_ATTACHMENTS_ADD_LOADING,
          payload,
          meta: { status: 'loading' },
        })

        const { event, files } = payload

        const base64Data = await Promise.all(files.map(toBase64))
        const base64Files = files.reduce(
          (acc, file, index) => ({ ...acc, [file.name]: base64Data[index] }),
          {},
        )

        const requestData = files.map((file) => ({
          feedback: event,
          mimeType: file.type,
          filename: file.name,
          fileContents: base64Files[file.name],
        }))

        const blob = new Blob(requestData.map(JSON.stringify))
        const blobSizeMB = blob.size / (1024 * 1024)

        if (blobSizeMB > REQUEST_SIZE_LIMIT_MB) {
          throw new Error(`Request size exceeds ${REQUEST_SIZE_LIMIT_MB}MB`)
        }

        const apiResult = await apiFetch(
          '/smoke_event_feedback_attachments/',
          requestData,
          { method: 'POST', cancelationPrefix: entityName },
        )

        await dispatch({
          type: FEEDBACK_ATTACHMENTS_ADD_LOADED,
          payload: apiResult,
          meta: { status: 'succeeded' },
        })
        return apiResult
      } catch (err) {
        if (!isAbortError(err)) {
          dispatch({
            type: FEEDBACK_ATTACHMENTS_ADD_FAILED,
            payload: err,
            meta: { status: 'failed' },
          })
          throw err
        }
        return null
      }
    },
  doDeleteFeedbackAttachments:
    (payload) =>
    async ({ dispatch, apiFetch }) => {
      try {
        dispatch({
          type: FEEDBACK_ATTACHMENTS_DELETE_LOADING,
          payload,
          meta: { status: 'loading' },
        })

        await apiFetch(
          '/smoke_event_feedback_attachments/',
          typeof payload === 'string'
            ? [{ id: payload }]
            : payload.map((id) => ({ id })),
          {
            method: 'DELETE',
            cancelationPrefix: entityName,
          },
        )

        await dispatch({
          type: FEEDBACK_ATTACHMENTS_DELETE_LOADED,
          payload: null,
          meta: { status: 'succeeded' },
        })
        return null
      } catch (err) {
        if (!isAbortError(err)) {
          dispatch({
            type: FEEDBACK_ATTACHMENTS_DELETE_FAILED,
            payload: err,
            meta: { status: 'failed' },
          })
          throw err
        }
        return null
      }
    },
}
